import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Lock } from '@phosphor-icons/react'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'

interface NavLinkProps {
  to: string
  children: React.ReactNode
  icon?: PhosphorIcon
  /** Optional `data-tour` attribute used to anchor the FirstLoginTour spotlight. */
  dataTour?: string
  /** When true, renders with a lock badge + muted style — used for paid
   *  features on free plans (BetterNotes on Starter-lock). */
  locked?: boolean
}

export default function NavLink({ to, children, icon: Icon, dataTour, locked = false }: NavLinkProps) {
  const location = useLocation()
  const isActive = location.pathname === to

  // Hard refresh on EVERY cross-page navigation from the Navbar. Rationale
  // (Mustapha 2026-04-21): "je veux un refresh partout, comme ça on ne se
  // casse pas la tête". Each page mounts with fresh state, zero risk of
  // soft-nav side-effects (stale caches, lingering Realtime channels,
  // setState-in-effect loops). BN-internal tab switches (entering another
  // `/betternotes/*` route while already on `/betternotes/*`) stay soft so
  // the shared BetterNotes shell (CalendarMeetings + RecordingsProvider)
  // doesn't re-mount between tabs of the same hub. Tab-style nav inside
  // Shop / Dashboard / etc uses `<Link>` directly, not this component.
  const stayingInBN =
    location.pathname.startsWith('/betternotes') && to.startsWith('/betternotes')
  const isSameRoute = location.pathname === to

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (stayingInBN || isSameRoute) return
    // Preserve new-tab / middle-click / mod-click behaviour.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    // Full-page navigation — drops every provider, Realtime channel and
    // per-page hook state so the destination mounts fresh.
    window.location.href = to
  }

  return (
    <Link
      to={to}
      data-tour={dataTour}
      onClick={handleClick}
      className={`group relative flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
        isActive
          ? 'text-[var(--bn-text-primary)]'
          : locked
            ? 'text-[var(--bn-text-subtle)] hover:text-[var(--bn-accent)] hover:bg-[var(--bn-accent-bg-weak)]'
            : 'text-[var(--bn-text-muted)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)]'
      }`}
    >
      {isActive && (
        <motion.div
          layoutId="navbar-active-bg"
          className="absolute inset-0 bg-[var(--bn-accent-bg)] rounded-lg -z-10"
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
      {Icon && (
        <Icon
          className={`w-[18px] h-[18px] mr-1.5 transition-transform duration-150 group-hover:scale-110 group-hover:-translate-y-px ${
            locked ? 'opacity-50' : 'opacity-70'
          }`}
          weight={isActive ? 'bold' : 'duotone'}
        />
      )}
      <span className={locked ? 'opacity-80' : undefined}>{children}</span>
      {locked && (
        <Lock
          className="ml-1.5 h-3 w-3 text-[var(--bn-accent)]/80"
          weight="fill"
          aria-label="Disponible sur les plans payants"
        />
      )}
      {isActive && (
        <motion.div
          layoutId="activeNavIndicator"
          className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--bn-accent)] rounded-full"
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
    </Link>
  )
}
